import * as array from 'd3-array'

/*
 * A more powerful bisect right that works for ascending and descending arrays.
 * d3's bisectRight expects ascending values.
 */
function bisectRight (ar, value, isAscending = true) {
  if (isAscending) {
    // For ascending order, use d3.bisectRight directly
    return array.bisectRight(ar, value)
  } else {
    // For descending order
    // Conceptually reverse the logic
    const reversedIndex = array.bisectRight(ar.slice().reverse(), value, 0, ar.length)

    // Adjust the index to match the original descending order
    // Note: No need to reverse the array physically; just calculate the correct index
    return ar.length - reversedIndex
  }
}

/*
 * Determines the foreground threshold arc by using the incoming data `colors` array
 * and comparing it with the data.value.color. This will always determine the threshold
 * arc based on the data sent by SB3.
 */
export default function (colors, value, color, thresholds) {
  // We have to determine which threshold should be used for this value. Start by matching the incoming color to the threshold colors
  let indices = colors.reduce((p, c, i) => {
      if (c == color) {
        p.push(i)
      }
      return p
    }, [])
    , idx = 0 // default - the color isn't in the thresholds, so we can't do a lookup
    , idxOffset = 1 // by default the ending threshold is right after the starting threshold
    , isAscending = thresholds[0] <= thresholds[thresholds.length - 1] // Assumes the array is consistently ordered and not mixed

  if (indices.length === 1) {
    idx = indices[0] // Simple case, we don't have duplicate colors
  } else if (indices.length === 2 && indices[1] - indices[0] === 1 && thresholds[indices[1]] === value) {
    // Duplicate colors (stablize metric) and the matched threshold colors are
    // touching, and the value is exactly between these thresholds
    // See https://github.com/SpiderStrategies/Scoreboard/issues/30870
    // and https://github.com/SpiderStrategies/Scoreboard/issues/31032
    idx = indices[0] // start at the first color that matches
    idxOffset = 2
  } else if (indices.length == 2 && indices[1] - indices[0] !== 1) {
    // This is a stabilize metric which has duplicate colors, and the matched
    // thresholds colors are not adjacent
    let rawIdx = array.bisectLeft(thresholds, value)

    // If the raw index is on the first half of thresholds, use the first color
    // otherwise use 2nd
    idx = rawIdx < thresholds.length / 2 ? indices[0] : indices[1]
  } else {
    // basic bisect
    idx = Math.max(bisectRight(thresholds, value, isAscending) - 1, 0)

    if (idx == thresholds.length - 1) {
      // Matches the final threshold. Make sure we start at the beginning of the last threshold
      idx -= 1
    }

    if (colors[idx] !== color) {
      // Determine if the threshold is on the right or left of the color
      // This is needed b/c we're using `bisectRight`, so it picked the right threshold.
      if (colors[idx - 1] === color) {
        idx -= 1
      } else {
        idx += 1
      }
    }
  }

  // We now know which color threshold to use based on the incoming data.value. Use it to grab the start/end values
  // from the thresholds array
  return {
    start: thresholds[idx],
    end: thresholds[idx + idxOffset],
    color
  }
}
